"use client";

import React, { useEffect, useState } from "react";
import { useEditor, EditorContent, ReactNodeViewRenderer } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { PercentTable } from "@/tina/fields/percentTable";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Link } from "@tiptap/extension-link";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Heading } from "@tiptap/extension-heading";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { all, createLowlight } from "lowlight";
import { cn } from "@/utils";
import { CodeBlockComponent } from "@/tina/fields/CodeBlockComponent";
import { VideoExtensionFrontend } from "@/tina/fields/VideoExtensionFrontend";
import { IframeExtensionFrontend } from "@/tina/fields/IframeExtensionFrontend";
import { ImageNodeFrontend } from "@/tina/fields/ImageNodeFrontend";
import { Image } from "@tiptap/extension-image";
import { TextAlign } from "@tiptap/extension-text-align";
import { Indent } from "@/tina/fields/IndentExtension";
import { ContentImageLightbox } from "@/components/ContentImageLightbox";

// Create lowlight instance with all common languages
const lowlight = createLowlight(all);

interface TiptapContentProps {
  content: string | any;
  className?: string;
}

export default function TiptapContent({
  content,
  className = "",
  ...props
}: TiptapContentProps) {
  const [imageLightbox, setImageLightbox] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  // Send postMessage to Tina admin on click
  const handleTinaClick = () => {
    if (typeof window !== "undefined") {
      window.parent.postMessage({ type: "tiptap-click" }, "*");
    }
  };

  // Parse content before hooks (avoid conditional hook calls)
  let jsonContent = null;

  if (content) {
    try {
      if (typeof content === "string") {
        jsonContent = JSON.parse(content);
      } else {
        jsonContent = content;
      }
    } catch (e) {
      console.error("Failed to parse Tiptap content:", e);
    }
  }

  // Create read-only editor with same extensions as TiptapField
  const extensions = [
    StarterKit.configure({
      heading: false,
      link: false,
      codeBlock: false,
      // Read-only: the trailing paragraph is an editing affordance, and it adds a
      // blank line whenever content ends on a heading, image, list or code block.
      trailingNode: false,
    }),
    Heading.extend({
      renderHTML({ node, HTMLAttributes }) {
        const level = node.attrs.level;
        const classMap: { [key: number]: string } = {
          2: "h3",
          3: "h4",
          4: "h4",
        };
        const className = classMap[level] || `h${level}`;

        return [
          `h${level}`,
          {
            ...HTMLAttributes,
            class: className,
          },
          0,
        ];
      },
    }).configure({
      levels: [2, 3, 4],
    }),
    Underline,
    TextStyle,
    Color,
    Link,
    PercentTable,
    TableRow,
    TableHeader,
    TableCell,
    CodeBlockLowlight.extend({
      addNodeView() {
        return ReactNodeViewRenderer((props) => (
          <CodeBlockComponent {...props} readOnly={true} />
        ));
      },
    }).configure({
      lowlight,
      defaultLanguage: "c",
      HTMLAttributes: {
        class: "tiptap-code-block",
      },
    }),
    Image.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          src: {
            default: null,
            parseHTML: (element) => element.getAttribute("src"),
            renderHTML: (attributes) => {
              if (!attributes.src) return {};
              return { src: attributes.src };
            },
          },
          alt: {
            default: null,
            parseHTML: (element) => element.getAttribute("alt"),
            renderHTML: (attributes) => {
              if (!attributes.alt) return {};
              return { alt: attributes.alt };
            },
          },
          caption: {
            default: "",
            parseHTML: (element) => element.getAttribute("data-caption") || "",
            renderHTML: (attributes) => {
              if (!attributes.caption) return {};
              return { "data-caption": attributes.caption };
            },
          },
          size: {
            default: "large",
            parseHTML: (element) =>
              element.getAttribute("data-size") || "large",
            renderHTML: (attributes) => {
              const size = attributes.size || "large";
              return {
                "data-size": size,
                class: `tiptap-image tiptap-image-${size}`,
              };
            },
          },
          transparent: {
            default: false,
            parseHTML: (element) =>
              element.getAttribute("data-transparent") === "true",
            renderHTML: (attributes) => {
              if (!attributes.transparent) return {};
              return { "data-transparent": "true" };
            },
          },
        };
      },
      renderHTML({ HTMLAttributes }) {
        const size =
          HTMLAttributes["data-size"] || HTMLAttributes.size || "large";
        const transparent = HTMLAttributes["data-transparent"] === "true";
        const classes = ["tiptap-image", `tiptap-image-${size}`];
        if (transparent) classes.push("tiptap-image-transparent");
        return [
          "img",
          {
            ...HTMLAttributes,
            "data-size": size,
            class: classes.join(" "),
          },
        ];
      },
      addNodeView() {
        return ReactNodeViewRenderer(ImageNodeFrontend);
      },
    }).configure({
      inline: false,
      allowBase64: true,
      HTMLAttributes: {
        class: "tiptap-image",
      },
    }),
    VideoExtensionFrontend,
    IframeExtensionFrontend,
    TextAlign.configure({
      types: ["heading", "paragraph", "listItem", "div"],
      alignments: ["left", "center", "right", "justify"],
      defaultAlignment: "left",
    }),
    Indent,
  ];

  const editor = useEditor({
    extensions: extensions as any, // Fix type error by casting to any
    content: jsonContent,
    editable: false,
    immediatelyRender: false, // Prevent SSR hydration mismatch
    editorProps: {
      attributes: {
        class: cn("tiptap-content markdown", className),
      },
    },
  });

  // Update editor content when the content prop changes
  useEffect(() => {
    if (editor && jsonContent) {
      // Use queueMicrotask to defer the update and avoid flushSync warning
      queueMicrotask(() => {
        if (editor && !editor.isDestroyed) {
          const currentContent = editor.getJSON();
          // Only update if content has actually changed
          if (JSON.stringify(currentContent) !== JSON.stringify(jsonContent)) {
            editor.commands.setContent(jsonContent);
          }
        }
      });
    }
  }, [content, editor, jsonContent]);

  // Legacy/migrated content may render bare <table> without TipTap's .tableWrapper; wrap so
  // global .markdown-table-wrapper styles (overflow-x) match MarkdownContent / advisories.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    const wrapBareTables = () => {
      const root = editor.view.dom as HTMLElement;
      root.querySelectorAll("table").forEach((table) => {
        if (table.closest(".tableWrapper, .markdown-table-wrapper")) return;
        const wrapper = document.createElement("div");
        wrapper.className = "markdown-table-wrapper overflow-x-auto w-full";
        table.parentNode?.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    };

    const run = () => {
      requestAnimationFrame(() => {
        if (!editor.isDestroyed) wrapBareTables();
      });
    };

    run();
    editor.on("update", run);
    return () => {
      editor.off("update", run);
    };
  }, [editor]);

  // Now it's safe to return null after all hooks have been called
  if (!editor || !jsonContent) {
    return null;
  }

  // Handle link clicks - open in new tab when in admin preview
  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const img = target.closest("img.tiptap-image");
    if (img instanceof HTMLImageElement && img.src) {
      e.preventDefault();
      e.stopPropagation();
      setImageLightbox({ src: img.currentSrc, alt: img.alt });
      return;
    }
    const link = target.closest("a.tiptap-link");
    if (link && link instanceof HTMLAnchorElement && link.href) {
      // Check if we're in an iframe (admin preview)
      const isInIframe = window.self !== window.top;
      if (isInIframe) {
        e.preventDefault();
        e.stopPropagation();
        window.open(link.href, "_blank", "noopener,noreferrer");
      }
      // Otherwise, let the link work normally on the frontend
    }
  };

  return (
    <div
      {...props}
      onClick={(e) => {
        handleClick(e);
        handleTinaClick();
      }}
      onClickCapture={() => {
        setTimeout(() => {
          window.parent.postMessage({ type: "tiptap-click" }, "*");
        }, 500);
      }}
    >
      <EditorContent editor={editor} />
      <ContentImageLightbox
        open={imageLightbox !== null}
        onOpenChange={(open) => {
          if (!open) setImageLightbox(null);
        }}
        src={imageLightbox?.src ?? ""}
        alt={imageLightbox?.alt ?? ""}
      />
    </div>
  );
}
